import { computed, reactive, ref, toRaw, watch } from 'vue'
import { useNuxtApp } from '#app'
import { useResponseError } from '~/composables/error'
import { result, cloneDeep, isEqual } from 'lodash'

export function useCrud<TModel extends Record<string, any>>(options: {
  endpoint: string
  defaultFormModel?: (() => TModel) | TModel
  serialize?: (model: TModel) => any
  deserialize?: (data: any) => TModel
}) {
  const { $api } = useNuxtApp()
  const { errorCode, errorMsg, formErrors, setErrors, clearErrors } = useResponseError()

  const model = ref<TModel>(cloneDeep(result(options, "defaultFormModel")))
  const formModel = reactive<TModel>(cloneDeep(result(options, "defaultFormModel")))

  const loadLoading = ref(false)
  const saveLoading = ref(false)
  const deleteLoading = ref(false)

  const isUpdate = computed(() => !!formModel.id)
  const isCreate = computed(() => !formModel.id)
  
  const isModified = ref(false)

  watch(
    () => [model.value, formModel],
    () => {
      const defaults = result(options, 'defaultFormModel') || {}
      isModified.value = Object.keys(defaults).some(key => 
        !isEqual((toRaw(model.value) as any)?.[key], (toRaw(formModel) as any)?.[key])
      )
    },
    { deep: true, immediate: true }
  )

  function reset() {
    clearErrors()
    const defaults = result(options, 'defaultFormModel')
    Object.keys(formModel).forEach(key => delete formModel[key])
    Object.assign(formModel, defaults)
  }

  async function load(id?: string) {
    loadLoading.value = true
    clearErrors()
    try {
      let data: any = await $api<TModel>(`${options.endpoint}/${id || formModel.id}`)
      if (options.deserialize) {
        data = options.deserialize(data)
      }
      model.value = data
      Object.assign(formModel, cloneDeep(data))
    } catch (e: any) {
      setErrors(e)
      throw e
    } finally {
      loadLoading.value = false
    }
  }

  async function save() {
    saveLoading.value = true
    clearErrors()
    try {
      const id = formModel.id
      const method = id ? 'PUT' : 'POST'
      const payload = options.serialize ? options.serialize(formModel as TModel) : formModel

      let data: any = await $api<TModel>(options.endpoint, {
        method,
        body: payload,
      })

      if (options.deserialize) {
        data = options.deserialize(data)
      }

      Object.assign(model.value, cloneDeep(data))
      Object.assign(formModel, cloneDeep(data))

      return data
    } catch (e: any) {
      setErrors(e)
      throw e
    } finally {
      saveLoading.value = false
    }
  }

  async function del(id?: string) {
    deleteLoading.value = true
    clearErrors()
    try {
      await $api(`${options.endpoint}/${id || formModel.id}`, { method: 'DELETE' })
    } catch (e: any) {
      setErrors(e)
      throw e
    } finally {
      deleteLoading.value = false
    }
  }

  return {
    errorCode,
    formErrors,
    errorMsg,

    model,
    formModel,

    loadLoading,
    saveLoading,
    deleteLoading,

    isUpdate,
    isCreate,
    isModified,
    
    reset,
    load,
    save,
    del,

    setErrors,
    clearErrors
  }
}

export type CrudResult<TModel extends Record<string, any>> = ReturnType<typeof useCrud<TModel>>
